import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  APIProvider,
  Map,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";

import { GS3HeaderLogo } from "@/assets/images";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from "@shared/config";
import { useLocalization } from "@i18n";
import { buildAssetUrl, resolveGoogleMapsLocale } from "@shared/utils";

type GeneralStructureMapProps = {
  structureId?: string;
  latitude: number | null;
  longitude: number | null;
  structureName: string;
  orthophotoStoragePath: string | null;
  zoom?: number;
};

const ORTHOPHOTO_BOUNDS_DELTA = 0.003;

const MAP_PIN_PATH =
  "M24 1C13.507 1 5 9.507 5 20c0 11.05 9.375 25.688 17.36 36.86a2.8 2.8 0 0 0 4.64 0C34.625 45.688 43 31.05 43 20 43 9.507 34.493 1 24 1z";

type MapLatLng = { lat: number; lng: number };

/** Satélite só na abertura — não reimpõe tipo/layers após o usuário mudar Mapa ou Marcadores. */
function MapInitialSatelliteType() {
  const map = useMap();
  const hasAppliedInitialTypeRef = useRef(false);

  useEffect(() => {
    if (!map || hasAppliedInitialTypeRef.current) {
      return;
    }

    map.setMapTypeId("satellite");
    hasAppliedInitialTypeRef.current = true;
  }, [map]);

  return null;
}

/** Recentraliza o mapa quando a estrutura/coordenadas mudam (defaultCenter só vale na montagem). */
function MapViewSync({
  center,
  zoom,
}: {
  center: MapLatLng;
  zoom: number;
}) {
  const map = useMap();
  const lastSyncedRef = useRef<{ lat: number; lng: number; zoom: number } | null>(
    null,
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    const lastSynced = lastSyncedRef.current;
    if (
      lastSynced &&
      lastSynced.lat === center.lat &&
      lastSynced.lng === center.lng &&
      lastSynced.zoom === zoom
    ) {
      return;
    }

    map.panTo(center);
    if (map.getZoom() !== zoom) {
      map.setZoom(zoom);
    }
    lastSyncedRef.current = { lat: center.lat, lng: center.lng, zoom };
  }, [map, center, zoom]);

  return null;
}

/** Mantém visão 2D (tilt 0) para o pin não distorcer com perspectiva ao dar zoom. */
function MapFlatViewLock() {
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const lockFlatView = () => {
      if (map.getTilt() !== 0) {
        map.setTilt(0);
      }
    };

    map.setTilt(0);
    const zoomListener = map.addListener("zoom_changed", lockFlatView);
    const tiltListener = map.addListener("tilt_changed", lockFlatView);

    return () => {
      zoomListener.remove();
      tiltListener.remove();
    };
  }, [map]);

  return null;
}

function OrthophotoGroundOverlay({
  orthophotoUrl,
  latitude,
  longitude,
}: {
  orthophotoUrl: string;
  latitude: number;
  longitude: number;
}) {
  const map = useMap();
  const coreLibrary = useMapsLibrary("core");
  const mapsLibrary = useMapsLibrary("maps");

  useEffect(() => {
    if (!map || !coreLibrary || !mapsLibrary) {
      return undefined;
    }

    const bounds = new coreLibrary.LatLngBounds(
      { lat: latitude - ORTHOPHOTO_BOUNDS_DELTA, lng: longitude - ORTHOPHOTO_BOUNDS_DELTA },
      { lat: latitude + ORTHOPHOTO_BOUNDS_DELTA, lng: longitude + ORTHOPHOTO_BOUNDS_DELTA },
    );

    const overlay = new mapsLibrary.GroundOverlay(orthophotoUrl, bounds, {
      opacity: 0.9,
    });
    overlay.setMap(map);

    return () => {
      overlay.setMap(null);
    };
  }, [map, coreLibrary, mapsLibrary, orthophotoUrl, latitude, longitude]);

  return null;
}

export function GeneralStructureMapPinContent({
  structureName,
  pinClipId,
}: {
  structureName: string;
  pinClipId: string;
}) {
  return (
    <div
      className="general-structure-map-pin"
      data-testid="general-map-pin"
      aria-label={structureName}
      title={structureName}
    >
      <p className="general-structure-map-pin__label">{structureName}</p>
      <svg
        className="general-structure-map-pin__shape"
        viewBox="0 0 48 56"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          <clipPath id={pinClipId}>
            <path d={MAP_PIN_PATH} />
          </clipPath>
        </defs>
        <path d={MAP_PIN_PATH} fill="#ffffff" />
        <image
          href={GS3HeaderLogo}
          x="4"
          y="4"
          width="40"
          height="46"
          clipPath={`url(#${pinClipId})`}
          preserveAspectRatio="xMidYMid meet"
        />
        <path
          d={MAP_PIN_PATH}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
}

/**
 * OverlayView posiciona o pin em pixels fixos na tela — não escala com o zoom
 * (comportamento que o AdvancedMarker não garante em zoom extremo).
 */
function GeneralStructureMapPin({
  position,
  structureName,
}: {
  position: MapLatLng;
  structureName: string;
}) {
  const map = useMap();
  const mapsLibrary = useMapsLibrary("maps");
  const pinClipId = `general-map-pin-clip-${useId().replace(/:/g, "")}`;
  const [overlayContainer, setOverlayContainer] = useState<HTMLDivElement | null>(
    null,
  );

  useEffect(() => {
    if (!map || !mapsLibrary) {
      return undefined;
    }

    const container = document.createElement("div");
    container.className = "general-structure-map-pin-overlay";

    class PinOverlay extends mapsLibrary.OverlayView {
      onAdd() {
        this.getPanes()?.floatPane.appendChild(container);
      }

      draw() {
        const projection = this.getProjection();
        if (!projection) {
          return;
        }

        const point = projection.fromLatLngToDivPixel({
          lat: position.lat,
          lng: position.lng,
        });

        if (point) {
          container.style.left = `${point.x}px`;
          container.style.top = `${point.y}px`;
        }
      }

      onRemove() {
        container.remove();
      }
    }

    const overlay = new PinOverlay();
    overlay.setMap(map);
    setOverlayContainer(container);

    return () => {
      overlay.setMap(null);
      setOverlayContainer(null);
    };
  }, [map, mapsLibrary, position.lat, position.lng]);

  if (!overlayContainer) {
    return null;
  }

  return createPortal(
    <GeneralStructureMapPinContent
      structureName={structureName}
      pinClipId={pinClipId}
    />,
    overlayContainer,
  );
}

type GeneralStructureMapCanvasProps = {
  googleMapsLocale: ReturnType<typeof resolveGoogleMapsLocale>;
  mapInstanceKey: string;
  center: MapLatLng;
  zoom: number;
  structureName: string;
  orthophotoUrl: string | null;
};

function GeneralStructureMapCanvas({
  googleMapsLocale,
  mapInstanceKey,
  center,
  zoom,
  structureName,
  orthophotoUrl,
}: GeneralStructureMapCanvasProps) {
  return (
    <APIProvider
      apiKey={GOOGLE_MAPS_API_KEY}
      language={googleMapsLocale.language}
      region={googleMapsLocale.region}
    >
      <div
        data-testid="general-structure-map"
        data-map-mode={orthophotoUrl ? "orthophoto" : "satellite"}
        className="h-full w-full overflow-hidden rounded-xl"
      >
        <Map
          key={mapInstanceKey}
          mapId={GOOGLE_MAPS_MAP_ID}
          defaultCenter={center}
          defaultZoom={zoom}
          defaultTilt={0}
          defaultHeading={0}
          gestureHandling="cooperative"
          disableDefaultUI={false}
          mapTypeControl
          streetViewControl
          fullscreenControl
          rotateControl={false}
          className="h-full w-full"
        >
          <MapViewSync center={center} zoom={zoom} />
          <MapInitialSatelliteType />
          <MapFlatViewLock />
          {orthophotoUrl ? (
            <OrthophotoGroundOverlay
              orthophotoUrl={orthophotoUrl}
              latitude={center.lat}
              longitude={center.lng}
            />
          ) : null}
          <GeneralStructureMapPin position={center} structureName={structureName} />
        </Map>
      </div>
    </APIProvider>
  );
}

export const GeneralStructureMap = ({
  structureId,
  latitude,
  longitude,
  structureName,
  orthophotoStoragePath,
  zoom = 15,
}: GeneralStructureMapProps) => {
  const { translate, language } = useLocalization();
  const googleMapsLocale = resolveGoogleMapsLocale(language);

  const center = useMemo(
    () =>
      latitude != null && longitude != null
        ? { lat: latitude, lng: longitude }
        : null,
    [latitude, longitude],
  );

  if (center == null) {
    return (
      <div
        data-testid="general-map-no-coordinates"
        className="flex h-full items-center justify-center rounded-xl bg-muted text-sm text-muted-foreground"
      >
        {translate("general.dashboard.map.noCoordinates")}
      </div>
    );
  }

  const orthophotoUrl = orthophotoStoragePath
    ? buildAssetUrl(orthophotoStoragePath)
    : null;
  const mapInstanceKey = structureId ?? `${center.lat},${center.lng}`;

  return (
    <GeneralStructureMapCanvas
      googleMapsLocale={googleMapsLocale}
      mapInstanceKey={mapInstanceKey}
      center={center}
      zoom={zoom}
      structureName={structureName}
      orthophotoUrl={orthophotoUrl}
    />
  );
};
